import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { retryPayment, toastError } from "../../api/services";
import "./OrderFailed.css";

function OrderFailed() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("orderId");
  const [retrying, setRetrying] = useState(false);
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
  const timer = setInterval(() => {
    setCountdown((prev) => {
      if (prev <= 1) {
        clearInterval(timer);
        // Optional: redirect to cart when countdown reaches 0
        // navigate('/cart');
        return 0;
      }
      return prev - 1;
    });
  }, 1000);

  return () => clearInterval(timer);
}, [navigate]);

  const handleRetry = async () => {
    if (!orderId) {
      toastError("Invalid order");
      return;
    }

    setRetrying(true);
    try {
      const response = await retryPayment(orderId);
      if (response?.data?.success) {
        navigate(`/payment?orderId=${orderId}`);
      } else {
        toastError(response?.data?.message || "Failed to retry payment");
      }
    } catch (error) {
      console.error("Retry error:", error);
      toastError("Something went wrong");
    } finally {
      setRetrying(false);
    }
  };

  const commonIssues = [
    "Insufficient balance in account",
    "Incorrect CVV or expiry date",
    "Transaction timeout due to slow internet",
    "Bank server temporarily unavailable",
    "Daily transaction limit exceeded",
  ];

  return (
    <div className="order-failed">
      <div className="failed-container">
        <div className="error-header">
          <div className="error-icon">
            <i className="fas fa-times-circle"></i>
          </div>
          <h1>Payment Failed</h1>
          <p>We couldn't process your payment</p>
          {orderId && (
            <div className="order-id-box">
              <span>Order ID: </span>
              <strong>#{orderId}</strong>
            </div>
          )}
        </div>

        <div className="error-details">
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            <div>
              <h4>What went wrong?</h4>
              <p>Your payment could not be completed due to an issue with the payment gateway.</p>
            </div>
          </div>

          <div className="common-issues">
            <h4>Common reasons for payment failure:</h4>
            <ul>
              {commonIssues.map((issue, index) => (
                <li key={index}>
                  <i className="fas fa-chevron-right"></i>
                  {issue}
                </li>
              ))}
            </ul>
          </div>

          <div className="suggestions">
            <h4>Try these solutions:</h4>
            <div className="suggestion-list">
              <div className="suggestion">
                <i className="fas fa-credit-card"></i>
                <div>
                  <strong>Use a different card</strong>
                  <p>Try using another credit/debit card or UPI app</p>
                </div>
              </div>
              <div className="suggestion">
                <i className="fas fa-wifi"></i>
                <div>
                  <strong>Check your internet connection</strong>
                  <p>Ensure you have a stable internet connection</p>
                </div>
              </div>
              <div className="suggestion">
                <i className="fas fa-clock"></i>
                <div>
                  <strong>Try after some time</strong>
                  <p>Bank servers might be temporarily down</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button onClick={handleRetry} className="btn-retry" disabled={retrying}>
            {retrying ? (
              <>
                <span className="spinner-small"></span>
                Retrying...
              </>
            ) : (
              <>
                <i className="fas fa-redo-alt"></i>
                Retry Payment
              </>
            )}
          </button>
          <Link to={`/checkout?orderId=${orderId}`} className="btn-edit-order">
            <i className="fas fa-edit"></i>
            Edit Order
          </Link>
          <Link to="/shop" className="btn-shop">
            <i className="fas fa-shopping-cart"></i>
            Continue Shopping
          </Link>
        </div>

        <div className="support-section">
          <p>Need assistance? Our support team is here to help!</p>
          <div className="support-links">
            <a href="tel:18001234567" className="support-link">
              <i className="fas fa-phone"></i>
              Call Us: 1800-123-4567
            </a>
            <a href="mailto:support@flipkart.com" className="support-link">
              <i className="fas fa-envelope"></i>
              Email Support
            </a>
            <a href="https://wa.me/1234567890" target="_blank" rel="noopener noreferrer" className="support-link">
              <i className="fab fa-whatsapp"></i>
              WhatsApp Chat
            </a>
          </div>
        </div>

        <div className="redirect-notice">
          {countdown > 0 ? (
            <>Redirecting to cart in {countdown} seconds...</>
          ) : (
            <Link to="/cart">Click here if not redirected</Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderFailed;